import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators'
import {MdbsCore} from "@/api/tsclient";
import store, {alertModule, currentUserModule, dataMigrationModule, studyDefinitionAdminModule} from "@/store";
import ILoginDto = MdbsCore.ILoginDto;
import LoginDto = MdbsCore.LoginDto;
import BaseModule from "@/store/modules/BaseModule";

const authClient = new MdbsCore.AuthClient();

@Module({namespaced: true, name: "auth", store: store})
export default class AuthModule extends VuexModule {
    private token: string | undefined = sessionStorage.getItem('token') ?? undefined;
    private refreshToken: string | undefined = sessionStorage.getItem('refreshToken') ?? undefined;
    errorMessage: string | undefined;

    get loggedIn() {
        return this.token != null;
    }

    get hasFailed() {
        return this.errorMessage != null;
    }

    @Mutation
    setToken(token: string) {
        this.token = token
        sessionStorage.setItem('token', this.token);
    }

    @Mutation
    setRefreshToken(refreshToken: string) {
        this.refreshToken = refreshToken;
        sessionStorage.setItem('refreshToken', this.refreshToken);
    }

    @Mutation
    removeToken() {
        this.token = undefined;
        sessionStorage.removeItem("token")
    }

    @Mutation
    removeRefreshToken() {
        this.refreshToken = undefined;
        sessionStorage.removeItem("refreshToken");
    }

    @Mutation
    loginFailed(message: string) {
        this.errorMessage = message;
    }

    @Mutation
    resetLoginFailed() {
        this.errorMessage = undefined;
    }

    @Action({rawError: true})
    async login(login: ILoginDto): Promise<boolean> {
        if (this.hasFailed)
            this.resetLoginFailed();
        try {
            const tokenDto = await authClient.login(new LoginDto(login));
            if (tokenDto.token != null) {
                this.setToken(tokenDto.token);
                this.setRefreshToken(tokenDto.refreshToken ?? "");
                await currentUserModule.load();
                BaseModule.isStarting = false;
                return true;
            } else {
                this.loginFailed("Kein Token erhalten.")
                this.removeToken();
                await currentUserModule.reset();
                return false;
            }
        } catch (e) {
            this.loginFailed("Fehler beim Login aufgetreten: " + JSON.stringify(e));
            this.removeToken();
            this.removeRefreshToken();
            await currentUserModule.reset();
            await alertModule.handleException(e);
        }
        return false;
    }

    @Action
    async loginAfterExternalRedirect(refreshToken: string) {
        if (refreshToken == null) {
            await currentUserModule.reset();
            return false;
        }

        this.setRefreshToken(refreshToken);

        try {
            const tokenDto = await authClient.refreshLogin();

            if (tokenDto.token == null) {
                await currentUserModule.reset();
                return false;
            }

            this.setToken(tokenDto.token);
            this.setRefreshToken(tokenDto.refreshToken ?? "");

            await currentUserModule.load();

            return true;
        } catch (exception) {
            await currentUserModule.reset();
        }

        return false;
    }

    @Action
    async setTokenForPasswordReset(passwordResetToken: string){
        if (passwordResetToken == null) {
            return false;
        }

        try {
            this.setToken(passwordResetToken);
            return true;
        } catch (exception) {
            console.log(exception);
            await currentUserModule.reset();
        }

        return false;
    }

    @Action({rawError: true})
    async logout() {
        this.removeToken();
        this.removeRefreshToken();
        await currentUserModule.reset();
        // Admin data outlives the session otherwise — audit entries of the previous user would still
        // be on screen after the next login.
        dataMigrationModule.reset();
        studyDefinitionAdminModule.reset();
    }

}
